import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { LazyDialog, lazyDialog } from '../enquiry/lazyDialog';

// The dialog body (fields, validation, Google's button, the Radix dialog) is its own chunk,
// fetched on the first open, or earlier when the browser is idle (preloadAuthDialog).
const authDialog = lazyDialog(() => import(/* webpackChunkName: "auth-dialog" */ './AuthDialog'));

/** Start loading the sign-in dialog now. Safe to call often. */
export const preloadAuthDialog = () => authDialog.preload();

const LOAD_ERROR = "Log in couldn't open just now. Check your connection and try again, or email hello@tappd.app.";

/**
 * The redesigned sign-in / sign-up modal (SLOT_MODEL on), mounted by components/AuthModal.jsx
 * with the same props: `isOpen`, `onClose()` (AuthContext.closeAuthModal), `initialMode`
 * ('login' | 'signup', from openAuthModal(mode)). Only this shell is in the main bundle; the
 * dialog loads on demand (a small "Loading…" panel shows on a first open before it arrives,
 * and a failed load says so and retries on the next open). The chunk is also fetched once the
 * browser is idle after start-up, so "Log in" usually opens at once.
 *
 * Focus returns to the element that had it when the dialog opened (the header's "Log in",
 * a page's "Sign in" button).
 */
export default function SiteAuthModal({ isOpen, onClose, initialMode = 'login' }) {
  const open = !!isOpen;
  const openerRef = useRef(null);

  // The opener, before the dialog moves focus (layout effect).
  useLayoutEffect(() => {
    if (open && typeof document !== 'undefined') openerRef.current = document.activeElement;
  }, [open]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.requestIdleCallback !== 'function') return undefined;
    const handle = window.requestIdleCallback(() => preloadAuthDialog(), { timeout: 4000 });
    return () => window.cancelIdleCallback?.(handle);
  }, []);

  const closePending = () => {
    onClose();
    const el = openerRef.current;
    if (el && el.isConnected && typeof el.focus === 'function') el.focus();
  };

  return (
    <LazyDialog
      dialog={authDialog}
      open={open}
      onClose={closePending}
      errorMessage={LOAD_ERROR}
      render={(AuthDialog) => (
        <AuthDialog open={open} onClose={onClose} initialMode={initialMode} returnFocusRef={openerRef} />
      )}
    />
  );
}
